import { BrowseStateBase } from "./browse-state-base";
import { IBrowseDataSource } from "./browse-data-source";
import { ISelectItem } from "winfakt-vue-components";
import { IExtraFieldQuickEditViaMenuData, IExtraFieldQuickEditViaMenuDataResult, IExtraFields } from "../models/extra-fields";
import { IAwsDocumentDataSource } from "@/models/base/aws-document";
import { i18n } from "@/setup/i18n-setup";
import { AuthService } from "@/services";
import { MODULE } from "@/config/modules";
import { IBrowseWithExtraData } from "@/services/browse-service";
import { copyToClipboard } from "@/utils/clipboard";
import { CustomButton } from "@/utils/buttons/custom-button";
import { getFixedCustomButtonsForBrowse } from "@/utils/buttons/fixed-custom-buttons";
import { UserConfigService } from "@/services/user-config-service";
import { processIframeUrl } from "@/utils/iframe";
import { BrowseButton } from "@/models/user-configs/browse-permissions";

export class BrowseState extends BrowseStateBase {
	searchEnabled = true;
	editButtonsHidden = false;
	controlsDisabled = true;
	dataSource:IBrowseDataSource<any>;

	extraFieldQuickEditData: null | IExtraFieldQuickEditViaMenuData = null;
	extraFieldQuickEditShowFieldSelect: boolean = false;
	extraFieldsEditModal:boolean = false;

	uploadDocumentItem:null | IAwsDocumentDataSource = null;

	activeCustomButton:CustomButton | null = null;
	showCustomButtonModal:boolean = false;
	customButtonModel:any[] | null = null;

	constructor(protected vue:Vue,
		dataSource:IBrowseDataSource<any>, browseData:IBrowseWithExtraData, useURLQueryParams: boolean, selectedItems:any[]) {
		super(vue, dataSource, browseData, useURLQueryParams, selectedItems);
		this.dataSource = dataSource;
	}

	public getButtonBarItems():ISelectItem<string | Function>[]{
		let items = super.getButtonBarItems();
		if (this.selectedItems.length == 0) return items;
		items.push(...this.getDocumentsButtonBarItems());
		items.push(...this.getExtraFieldButtonBarItems());
		return items;
	}

	public getContextMenuItems():ISelectItem<string | Function>[]{
		let items = super.getContextMenuItems();
		items.push(...this.getDocumentsButtonBarItems());
		items.push(...this.getExtraFieldButtonBarItems());
		items.push(this.getCopyFieldContextMenuItem());
		return items;
	}

	private getCopyFieldContextMenuItem():ISelectItem<Function> {
		return {
			text: i18n.t("components.browse.copy-field-to-clipboard"),
			icon: "copy",
			disabled: !this.contextMenu.clickedCellText,
			value: ()=>{
				let text = this.contextMenu.clickedCellText;
				if (!text) return;
				copyToClipboard(text);
				this.wf.notify(i18n.t("components.browse.copied-to-clipboard").toString(), "success", 3000, "check");
			}
		};
	}

	private getExtraFieldButtonBarItems():ISelectItem<string | Function>[] {
		if(!this.dataSource.extraFieldSettings) return [];
		let baseItem:ISelectItem<string>[] = this.dataSource.extraFieldSettings.nameSettings.getFieldsEditableViaBrowseMenuMenuItems();
		let resultItems:ISelectItem<Function>[] = baseItem.map(event=>{
			let baseItem:ISelectItem<Function> = {
				text: event.text,
				icon: event.icon,
				disabled: event.disabled,
				value: ()=>{}
			};
			if(event.value.startsWith("quickEditExtra")){
				baseItem.value = (item:any)=>{
					if (item && this.selectedItems.length <= 1) {
						this.selectedItems.splice(0);
						this.selectedItems.push(item);
					}
					this.extraFieldQuickEditShowFieldSelect = true;
					this.contextMenu.close();
				};
			}else{
				baseItem.value = (item:any)=>{
					let field = event.value;
					this.quickEditExtraField(field, item);
				};
			}
			return baseItem;
		});
		if (!this.dataSource.extraFieldSettings.nameSettings.HideEditInBrowse) {
			resultItems.unshift({
				text: i18n.t("components.browse.edit-extra-fields"),
				value: this.openExtraFieldsEditModal.bind(this),
				icon: "file",
				disabled: !AuthService.hasModule(MODULE.EXTRA_VELDEN) || this.selectedItems.length > 1
			});
		}
		return resultItems;
	}

	areExtraFieldsFetched(item:any){
		if (this.dataSource.extraFieldSettings && this.dataSource.extraFieldSettings.areFetched) {
			return this.dataSource.extraFieldSettings.areFetched(item);
		}
		return this.selectedItems[0].ExtraFields || this.selectedItems[0].ExtraFields.ID != 0;
	}

	private async openExtraFieldsEditModal(item?:any){
		if (item && this.selectedItems.length <= 1) {
			this.selectedItems.splice(0);
			this.selectedItems.push(item);
		}
		if (!this.areExtraFieldsFetched(this.selectedItems[0])) {
			this.selectedItems[0].ExtraFields = await this.dataSource.getFullElement(this.selectedItems[0]);
		}
		this.extraFieldsEditModal = true;
		this.contextMenu.close();
	}

	private getDocumentsButtonBarItems():ISelectItem<string | Function>[]{
		if (!this.dataSource.getDocumentsDataSource) return [];
		return [{
			text: i18n.t("components.browse.manage-documents"),
			value: (v:any)=>{
				if (!this.dataSource.getDocumentsDataSource) return;
				if (v) {
					this.uploadDocumentItem = this.dataSource.getDocumentsDataSource(v);
				}
				let selectedItem =	this.selectedItems[0];
				if (!selectedItem) return null;
				this.uploadDocumentItem =  this.dataSource.getDocumentsDataSource(selectedItem);
			},
			icon: "file"
		}];
	}

	quickEditExtraField(field:string, item?:any){
		if(!this.dataSource.extraFieldSettings) return [];
		let conf = this.dataSource.extraFieldSettings.nameSettings;
		if (item && this.selectedItems.length <= 1) {
			this.selectedItems.splice(0);
			this.selectedItems.push(item);
		}
		let name = conf.getFieldName(field);
		let fieldConf = conf.getFieldConfig(field);
		this.extraFieldQuickEditData = {
			field,
			name,
			config: fieldConf
		};
	}

	delete() {
		this.emit("delete");
	}

	public getVisibleCustomButtons():CustomButton[] {
		let buttons = [
			...UserConfigService.getCustomButtonsForBrowse(this.browse.ID),
			...getFixedCustomButtonsForBrowse(this.browse.ID)
		];
		return buttons.filter(btn => !btn.AdminOnly || AuthService.isDevModeEnabled);
	}

	public getDoubleClickCustomButton():CustomButton | null {
		return this.getVisibleCustomButtons().find(btn => btn.OpenOnDoubleClick) || null;
	}

	public isButtonHidden(button:BrowseButton):boolean {
		try {
			return this.dataSource.browsePermissions().isButtonHidden(button);
		} catch (err) {
			return false;
		}
	}

	private isInlineIframeOpenFor(btn:CustomButton):boolean {
		let open = this.inlineIframeButton;
		if (!open || !this.inlineIframeUrl) return false;
		if (btn.ID && open.ID) return btn.ID === open.ID;
		return btn.Url === open.Url && btn.Name === open.Name;
	}

	public openCustomButton(btn:CustomButton, model:any[] | null = null) {
		let clickedFromButton = !model;
		if (!model) {
			model = this.selectedItems.length > 0 ? this.selectedItems : null;
		}
		if (btn.IntegrateInBrowse) {
			if (clickedFromButton && this.isInlineIframeOpenFor(btn)) {
				this.closeInlineIframe();
				return;
			}
			let url = processIframeUrl(btn.Url, {useElectronServerAsProxy: btn.UseDesktopAppAsProxy});
			if (model && model.length > 0) {
				const ids = model.map((item:any) => item.ID).join(",");
				url = url.replace("{id}", ids);
			}
			this.setInlineIframe(url, btn, model);
			return;
		}
		this.customButtonModel = model;
		this.activeCustomButton = btn;
		this.showCustomButtonModal = true;
	}

	edit() {
		if (this.selectedItems.length > 1 && this.dataSource.batchEdit) {
			this.dataSource.batchEdit(this.selectedItems);
		} else {
			this.dataSource.edit(this.selectedItems[0]);
		}
	}

	duplicate() {
		if(this.dataSource.duplicate){
			this.dataSource.duplicate(this.selectedItems[0]);
		}
	}

	emptySelectedItems() {
		this.selectedItems.splice(0, this.selectedItems.length);
	}

	async setExtraFieldQuickEditValue(value:IExtraFieldQuickEditViaMenuDataResult, callback:Function){
		if (this.selectedItems.length == 0) return;
		if (!this.dataSource.extraFieldSettings) return;
		if (!this.extraFieldQuickEditData) return;
		try{
			await this.dataSource.extraFieldSettings.batchEdit(this.selectedItems, this.extraFieldQuickEditData.field, value);
			this.extraFieldQuickEditData = null;
			await this.fetch();
			this.selectedItems.splice(0);
		}catch(err){

		}finally{
			callback();
		}
	}

	async getFirstSelectedExtraFields():Promise<IExtraFields | null>{
		if (this.selectedItems.length == 0) {
			return null;
		}
		let item = this.selectedItems[0];
		if (item.ExtraFields && item.ExtraFields.ID != 0) {
			return item.ExtraFields;
		}
		item = await this.dataSource.getFullElement(item);
		return item.ExtraFields;
	}

	async onDocumentUploadClosed(){
		this.fetch();
		this.uploadDocumentItem = null;
	}

	async updateExtraFields() {
		if (!this.dataSource.extraFieldSettings) return;
		await this.dataSource.extraFieldSettings.edit(this.selectedItems[0], this.selectedItems[0].ExtraFields);
		this.extraFieldsEditModal = false;
	}

	protected onColClickInterceptor(value:any, handler:any){
		let result:string | void = handler(value);
		if (!result) return;
		let parts = result.split("/");
		if (parts[0] == "edit-extra-field"){
			this.quickEditExtraField(parts[1].split(".").pop() || "", value);
		}else {
			this.emit("col-click-result", result, value);
		}
	}
}
